import React, { useEffect, useState } from 'react';
import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  Stack,
  TextField,
  Typography,
  TypographyProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';

import { LoadingButton } from '@mui/lab';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Countries } from '../../../../constants';
import { useAppSelector } from '../../../../hooks';
import {
  getAccessLimitState,
  toggleLeadGenModal,
} from '../../../../store/AccessLimitSlice';
import { getAuthState, setAuthUser } from '../../../../store/AuthSlice';
import { getAuthUserFromLocalStorage } from '../../../../helper';
import { getUsageByUserId } from '../../../../store/AccessLimitSlice/AccessLimitApi';
import { createAnalytics } from '../../../../store/AnalyticsSlice';

const buttonProps: ButtonProps = {
  style: { fontWeight: 600, padding: '6px 40px', marginLeft: '15px' },
  variant: 'contained',
};

const label: TypographyProps = {
  variant: 'subtitle2',
  fontWeight: 600,
  sx: { mb: '2px' },
};

// DATA
const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  ? Intl.DateTimeFormat().resolvedOptions().timeZone
  : 'Asia/Singapore';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let defaultCountryCode: any = 'SG';

const defaultUserTimezone = Countries.find(
  (country) => country.value === timezone
);

if (defaultUserTimezone && defaultUserTimezone.countryCode) {
  defaultCountryCode = defaultUserTimezone.countryCode;
}

const isEmailValid = (email: string) => {
  const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return email.toLocaleLowerCase().match(regex);
};

export interface ILeadGenState {
  email: string;
  fullName: string;
  phoneNumber: string;
  country: string;
}

interface LeadGenModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (values: ILeadGenState) => void;
}

const initialState: ILeadGenState = {
  email: '',
  fullName: '',
  phoneNumber: '',
  country: timezone,
};

const defaultErrorData = {
  visible: false,
  message: '',
};

const boxStyles: BoxProps['sx'] = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '15%',
  width: 32,
  height: 32,
};

const LeadGenModal: React.FC<LeadGenModalProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const theme = useTheme();
  const belowMd = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const location = useLocation();
  const isQuizModal = window.location.href.includes('quiz');

  // Store
  const accessLimitState = useAppSelector(getAccessLimitState);
  const { user } = useAppSelector(getAuthState);

  const { isLeadGenSuccess, status } = accessLimitState;

  // State
  const [state, setState] = useState(initialState);
  const [errors, setErrors] = useState({
    fullName: { ...defaultErrorData },
    email: { ...defaultErrorData },
    phoneNumber: { ...defaultErrorData },
  });

  useEffect(() => {
    if (isLeadGenSuccess) {
      const authUser = getAuthUserFromLocalStorage();
      if (authUser) {
        dispatch(setAuthUser(authUser));
        dispatch(getUsageByUserId(authUser._id));
      }
      dispatch(toggleLeadGenModal());
    }
  }, [isLeadGenSuccess]);

  useEffect(() => {
    if (state.fullName) {
      let _nameError = { ...defaultErrorData };

      if (state.fullName.length < 3) {
        _nameError = {
          visible: true,
          message: 'Name should have at least 3 characters',
        };
      }

      setErrors((prevState) => ({
        ...prevState,
        fullName: _nameError,
      }));
    }
  }, [state.fullName]);

  useEffect(() => {
    if (state.email) {
      let _emailError = { ...defaultErrorData };

      if (!isEmailValid(state.email)) {
        _emailError = {
          visible: true,
          message: 'Invalid Email',
        };
      }

      setErrors((prevState) => ({
        ...prevState,
        email: _emailError,
      }));
    }
  }, [state.email]);

  useEffect(() => {
    if (state.phoneNumber) {
      let _nameError = { ...defaultErrorData };

      if (state.phoneNumber.length < 9) {
        _nameError = {
          visible: true,
          message: 'Phone Number should have at least 9 numbers',
        };
      }

      setErrors((prevState) => ({
        ...prevState,
        phoneNumber: _nameError,
      }));
    }
  }, [state.phoneNumber]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.currentTarget;
    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleButtonClick = () => {
    dispatch(
      createAnalytics({
        type: 'LP - Leadgen Form',
        data: {
          selectedRole: user?.role.name,
          fullName: state.fullName,
          phoneNumber: state.phoneNumber,
          country: state.country,
          source: 'Quiz',
          url: location.pathname.replace('/learning-portal/dashboard/', ''),
        },
        userId: user?._id,
      })
    );
    onConfirm(state);
  };

  const submitButtonDisabled =
    state.phoneNumber.trim().length < 9 ||
    state.fullName.trim().length < 3 ||
    state.email.length === 0 ||
    !isEmailValid(state.email);

  return (
    <Dialog
      open={open}
      hideBackdrop
      onClose={onClose}
      PaperProps={{
        sx: {
          maxWidth: !belowMd && isQuizModal ? '800px' : '550px',
          borderRadius: '15px',
        },
      }}
    >
      <Stack direction="row">
        {!belowMd && isQuizModal && (
          <div
            style={{
              width: '50%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                height: '100%',
                margin: '10px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                borderRadius: '15px',
                alignItems: 'center',
                background: '#F1F5F9',
              }}
            >
              <DialogTitle fontWeight="600" align="center" marginBottom={4}>
                Unlock Your Quiz Results & AI Feedback!
              </DialogTitle>
              <Stack spacing={3}>
                <Stack spacing={2} alignItems="center" direction="row">
                  <Box sx={boxStyles} bgcolor="#D3D3D38A">
                    <img
                      src={`${process.env.PUBLIC_URL}/images/leadGenModal/chart.png`}
                      alt="chart"
                      style={{ width: 20, height: 20 }}
                    />
                  </Box>
                  <Typography fontSize={15}>
                    Get{' '}
                    <span style={{ fontWeight: 'bold' }}>instant results</span>
                  </Typography>
                </Stack>
                <Stack spacing={2} alignItems="center" direction="row">
                  <Box sx={boxStyles} bgcolor="#DDA0DD4A">
                    <img
                      src={`${process.env.PUBLIC_URL}/images/leadGenModal/brain.png`}
                      alt="brain"
                      style={{ width: 20, height: 20 }}
                    />
                  </Box>
                  <Typography fontSize={15}>
                    Review a detailed{' '}
                    <span style={{ fontWeight: 'bold' }}>
                      AI-powered <br />
                      marking{' '}
                    </span>
                    scheme
                  </Typography>
                </Stack>
                <Stack
                  spacing={2}
                  alignItems="center"
                  direction="row"
                  paddingBottom={5}
                >
                  <Box sx={boxStyles} bgcolor="#FFD7004A">
                    <img
                      src={`${process.env.PUBLIC_URL}/images/leadGenModal/star.png`}
                      alt="star"
                      style={{ width: 20, height: 20 }}
                    />
                  </Box>
                  <Typography fontSize={15}>
                    Review{' '}
                    <span style={{ fontWeight: 'bold' }}>
                      personalized tips{' '}
                    </span>
                    to <br /> booset your learning!
                  </Typography>
                </Stack>
              </Stack>
            </div>
          </div>
        )}
        <div style={{ width: belowMd || !isQuizModal ? '100%' : '50%' }}>
          {(belowMd || !isQuizModal) && (
            <DialogTitle fontWeight="600" align="center">
              Complete Your Learning Quest! 🏆
            </DialogTitle>
          )}
          <DialogContent>
            {(belowMd || !isQuizModal) && (
              <DialogContentText
                fontSize="12px"
                fontWeight="600"
                align="center"
                marginBottom="30px"
              >
                You&apos;ve completed one level! 🎉 Fill in your details to
                unlock the next level and gain access to exclusive tips and
                challenges! 🚀
              </DialogContentText>
            )}
            <Grid
              container
              spacing={1}
              sx={{ marginTop: !belowMd && isQuizModal ? '30px' : null }}
            >
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <FormControl fullWidth>
                  <Typography {...label}>Full Name *</Typography>
                  <TextField
                    size="small"
                    name="fullName"
                    type="text"
                    placeholder="Type your full name"
                    onChange={handleInputChange}
                    error={errors.fullName.visible}
                    helperText={errors.fullName.message || ' '}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <FormControl fullWidth>
                  <Typography {...label}>Email *</Typography>
                  <TextField
                    size="small"
                    name="email"
                    type="email"
                    placeholder="example@gmail.com"
                    onChange={handleInputChange}
                    error={errors.email.visible}
                    helperText={errors.email.message || ' '}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <Typography {...label}>Phone Number *</Typography>
                <PhoneInput
                  international
                  withCountryCallingCode
                  defaultCountry={defaultCountryCode}
                  placeholder="Enter phone number"
                  value={state.phoneNumber}
                  onChange={(value) => {
                    if (value) {
                      setState((prevState) => ({
                        ...prevState,
                        phoneNumber: value,
                      }));
                    }
                  }}
                  className="phone-number-input-custom-styles"
                />
                <FormHelperText
                  style={{ marginLeft: '14px', color: '#FF2B2D' }}
                >
                  {errors.phoneNumber.message || ' '}
                </FormHelperText>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions style={{ padding: '24px' }}>
            <Button {...buttonProps} color="inherit" onClick={onClose}>
              Cancel
            </Button>
            <LoadingButton
              {...buttonProps}
              color="primary"
              disabled={submitButtonDisabled}
              onClick={handleButtonClick}
              loading={status === 'loading'}
            >
              Submit
            </LoadingButton>
          </DialogActions>
        </div>
      </Stack>
    </Dialog>
  );
};

export default LeadGenModal;
